import { useSuspenseQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { appendDateTimestamp } from '@/utils/appendDateTimestamp';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type PlayableInvoiceRequestBody =
  paths['/playable-invoices']['post']['requestBody']['content']['application/json'];
export type PlayableInvoiceResponse =
  paths['/playable-invoices']['post']['responses']['200']['content']['application/json'];
export type PlayableInvoiceFetchOptions = paths['/playable-invoices']['post']['parameters']['query'];
export type PlayableInvoiceSearchFilters = PlayableInvoiceRequestBody['search'];
export type PlayableInvoice = PlayableInvoiceResponse['data']['invoices'][number];

export interface PlayableInvoiceParams {
  makerOrganizationUuid: string;
  options?: PlayableInvoiceFetchOptions;
  search?: PlayableInvoiceSearchFilters;
  takerMarkets: TakerMarket[];
}

const fetchPlayableInvoices = async ({
  makerOrganizationUuid,
  options,
  search,
  takerMarkets,
}: PlayableInvoiceParams): Promise<PlayableInvoiceResponse['data']> => {
  const limit = options?.limit ?? 100;
  const params = qs.stringify({ ...options, limit }, { addQueryPrefix: true });

  const response = await apiClient
    .post(`api/supplier-experience/playable-invoices${params}`, {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((takerMarket) => ({
          marketUuid: takerMarket.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
        })),
        ...(search ? { search } : {}),
      } satisfies PlayableInvoiceRequestBody,
    })
    .json<PlayableInvoiceResponse>();

  return {
    ...response.data,
    invoices: response.data.invoices.map((invoice) => ({
      ...invoice,
      originalDepositDate: invoice.originalDepositDate ? appendDateTimestamp(invoice.originalDepositDate) : null,
    })),
  };
};

const usePlayableInvoices = (params: PlayableInvoiceParams & { takerMarkets: TakerMarket[] }) => {
  const { makerOrganizationUuid, options, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['playable-invoices', makerOrganizationUuid, options, search, ...takerMarketUuidsMap]
      : ['playable-invoices-invalid'],
    queryFn: () => (isValid ? fetchPlayableInvoices(params) : null),
    staleTime: 0,
    gcTime: 0,
  });
};

export default usePlayableInvoices;
